import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtMoney, fmtCompact, fmtDate } from "@/lib/format";
import { Plus, Trash2, X, Link2, ChevronDown, ChevronRight, Search, Pencil, Sparkles } from "lucide-react";
import { toast } from "sonner";

const CURRENCIES = ["USD", "INR", "AED", "EUR", "GBP", "SAR"];

const STATUS_STYLES = {
  paid: "bg-[#047857] text-white",
  sent: "bg-[#0A0A0A] text-white",
  overdue: "bg-[#E53E3E] text-white",
  draft: "bg-[#F5F5F5] text-[#666] border border-[#E5E5E5]",
  partially_paid: "bg-[#D97706] text-white",
  void: "bg-[#999] text-white",
  open: "bg-[#0A0A0A] text-white",
  closed: "bg-[#047857] text-white",
};

const StatusPill = ({ value }) => {
  const v = (value || "").toLowerCase();
  const cls = STATUS_STYLES[v] || "bg-[#F5F5F5] text-[#666] border border-[#E5E5E5]";
  return (
    <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cls}`}>
      {v.replace(/_/g, " ") || "—"}
    </span>
  );
};

const Contracts = () => {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [linkingFor, setLinkingFor] = useState(null);  // contract object
  const [editing, setEditing] = useState(null);  // contract being edited
  const [expanded, setExpanded] = useState({});
  const [autoImporting, setAutoImporting] = useState(false);
  const [q, setQ] = useState("");

  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({
    customer_id: "",
    customer_name: "",
    po_number: "",
    contract_value: "",
    currency: "USD",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: "",
    notes: "",
  });

  const loadContracts = async () => {
    setLoading(true);
    try {
      const r = await api.get("/contracts");
      setRows(r.data.contracts || []);
      setTotals(r.data.totals);
    } catch (e) {
      toast.error("Failed to load contracts");
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const r = await api.get("/contracts/zoho-customers");
      setCustomers(r.data.customers || []);
    } catch {
      // silent
    }
  };

  useEffect(() => {
    loadContracts();
    loadCustomers();
  }, []);

  const onCustomerSelect = (cid) => {
    const c = customers.find((x) => x.customer_id === cid);
    setForm((f) => ({
      ...f,
      customer_id: cid,
      customer_name: c ? c.customer_name : "",
      currency: c?.currency || f.currency,
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.po_number || !form.contract_value) {
      toast.error("Customer, PO Number, and Contract Value are required");
      return;
    }
    try {
      const payload = {
        ...form,
        contract_value: parseFloat(form.contract_value),
        end_date: form.end_date || null,
        notes: form.notes || null,
      };
      if (editing) {
        await api.put(`/contracts/${editing.id}`, payload);
        toast.success("Contract updated");
      } else {
        await api.post("/contracts", payload);
        toast.success("Contract added");
      }
      setShowForm(false);
      setEditing(null);
      setForm({ customer_id: "", customer_name: "", po_number: "", contract_value: "", currency: "USD",
        start_date: new Date().toISOString().slice(0, 10), end_date: "", notes: "" });
      await loadContracts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save contract");
    }
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      customer_id: c.customer_id || "",
      customer_name: c.customer_name || "",
      po_number: c.po_number || "",
      contract_value: String(c.contract_value ?? ""),
      currency: c.currency || "USD",
      start_date: c.start_date || new Date().toISOString().slice(0, 10),
      end_date: c.end_date || "",
      notes: c.notes || "",
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this contract? Linked invoices stay in Zoho — only the contract record + links are removed.")) return;
    try {
      await api.delete(`/contracts/${id}`);
      toast.success("Contract deleted");
      await loadContracts();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleUnlinkInvoice = async (cid, invId) => {
    try {
      await api.delete(`/contracts/${cid}/invoices/${invId}`);
      toast.success("Invoice unlinked");
      await loadContracts();
    } catch {
      toast.error("Failed to unlink");
    }
  };

  const handleAutoImport = async () => {
    if (!window.confirm("Auto-import Contracts from Zoho invoices?\n\nEach unique PO/reference number on your invoices will become a Contract. Existing contracts with matching PO numbers will pick up any new invoices.")) return;
    setAutoImporting(true);
    try {
      const r = await api.post("/contracts/auto-import");
      const d = r.data;
      toast.success(`Auto-import complete · Created ${d.created} · Updated ${d.updated} · Linked ${d.invoices_linked} invoices${d.invoices_without_po > 0 ? ` · ${d.invoices_without_po} invoices had no PO (skipped)` : ""}`);
      await loadContracts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-import failed");
    } finally {
      setAutoImporting(false);
    }
  };

  const filtered = useMemo(() => {
    if (!q) return rows;
    const qq = q.toLowerCase();
    return rows.filter((r) =>
      (r.po_number || "").toLowerCase().includes(qq) ||
      (r.customer_name || "").toLowerCase().includes(qq)
    );
  }, [rows, q]);

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-[#666] mb-2">
            Manual entry · Linked to Zoho Books invoices
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter">
            Contracts &amp; POs
          </h1>
          <div className="text-sm font-mono text-[#666] mt-2">
            Track contract value per customer, link invoices from Zoho, see Billed · Collected · Balance.
          </div>
        </div>
        <div className="flex gap-3 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
            <input
              data-testid="contract-search"
              placeholder="Search PO# or customer..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="border border-[#E5E5E5] bg-white pl-9 pr-3 py-2 text-sm focus:border-[#1E3A8A] outline-none w-64"
            />
          </div>
          <button
            onClick={handleAutoImport}
            disabled={autoImporting}
            data-testid="auto-import-button"
            className="border-2 border-[#B8860B] text-[#B8860B] px-4 py-2 font-medium tracking-wide text-sm hover:bg-[#B8860B] hover:text-white transition-colors flex items-center gap-2 disabled:opacity-60"
          >
            <Sparkles className={`w-4 h-4 ${autoImporting ? "animate-pulse" : ""}`} />
            {autoImporting ? "Importing..." : "Auto-Import from Zoho"}
          </button>
          <button
            onClick={() => setShowForm(true)}
            data-testid="add-contract-button"
            className="bg-[#1E3A8A] text-white px-4 py-2 font-medium tracking-wide text-sm hover:bg-[#172a63] flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add Contract
          </button>
        </div>
      </div>

      {/* Totals strip */}
      {totals && totals.count > 0 && (
        <section className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: "Contracts", value: totals.count, raw: true, testId: "totals-count" },
            { label: "Contract Value", value: fmtCompact(totals.contract_value_usd, "USD"), testId: "totals-contract" },
            { label: "Billed", value: fmtCompact(totals.billed_usd, "USD"), testId: "totals-billed" },
            { label: "Collected", value: fmtCompact(totals.collected_usd, "USD"), accent: "pos", testId: "totals-collected" },
            { label: "Balance to Bill", value: fmtCompact(totals.balance_to_bill_usd, "USD"), accent: "neg", testId: "totals-balance" },
          ].map((c) => (
            <div key={c.label} data-testid={c.testId} className="bg-white border border-[#E5E5E5] border-t-[3px] border-t-black p-4">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-2">{c.label}</div>
              <div className={`text-2xl font-mono font-medium tracking-tight ${c.accent === "neg" ? "text-[#E53E3E]" : c.accent === "pos" ? "text-[#047857]" : "text-[#0A0A0A]"}`}>
                {c.value}
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Contracts table */}
      <div className="bg-white border border-[#E5E5E5] overflow-x-auto" data-testid="contracts-table">
        <table className="w-full border-collapse min-w-[1000px]">
          <thead>
            <tr className="bg-[#F5F5F5] border-b border-[#0A0A0A]">
              <th className="w-10"></th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">PO / Contract #</th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Customer</th>
              <th className="text-right text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Contract Value</th>
              <th className="text-right text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Billed (USD)</th>
              <th className="text-right text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Collected</th>
              <th className="text-right text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Balance to Bill</th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">% Billed</th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Status</th>
              <th className="text-right text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={10} className="text-center text-sm text-[#666] py-12">
                {loading ? "Loading..." : "No contracts yet. Click + Add Contract to get started."}
              </td></tr>
            ) : (
              filtered.map((c) => (
                <React.Fragment key={c.id}>
                  <tr className="border-b border-[#E5E5E5] hover:bg-[#F9F9F9]" data-testid={`contract-row-${c.id}`}>
                    <td className="py-3 px-2">
                      <button
                        onClick={() => setExpanded((ex) => ({ ...ex, [c.id]: !ex[c.id] }))}
                        data-testid={`expand-${c.id}`}
                        className="p-1 hover:bg-[#F0F0F0]"
                      >
                        {expanded[c.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="py-3 px-4 text-sm font-mono font-medium">
                      <div className="flex items-center gap-2">
                        {c.po_number}
                        {c.auto_imported && (
                          <span className="text-[9px] font-semibold uppercase tracking-wider bg-[#B8860B] text-white px-1.5 py-0.5" title="Auto-imported from Zoho invoices">
                            Auto
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm">{c.customer_name}</td>
                    <td className="py-3 px-4 text-sm font-mono text-right">
                      <div>{fmtMoney(c.contract_value, c.currency)}</div>
                      {c.currency !== "USD" && <div className="text-xs text-[#666]">{fmtMoney(c.contract_value_usd, "USD")}</div>}
                    </td>
                    <td className="py-3 px-4 text-sm font-mono text-right">{fmtMoney(c.billed_usd, "USD")}</td>
                    <td className="py-3 px-4 text-sm font-mono text-right text-[#047857]">{fmtMoney(c.collected_usd, "USD")}</td>
                    <td className="py-3 px-4 text-sm font-mono text-right font-semibold">{fmtMoney(c.balance_to_bill_usd, "USD")}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-[#F0F0F0]">
                          <div className="h-full bg-[#0A0A0A]" style={{ width: `${Math.min(c.pct_billed, 100)}%` }} />
                        </div>
                        <span className="text-xs font-mono text-[#666]">{c.pct_billed.toFixed(0)}%</span>
                      </div>
                    </td>
                    <td className="py-3 px-4"><StatusPill value={c.status} /></td>
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      <button
                        onClick={() => openEdit(c)}
                        data-testid={`edit-contract-${c.id}`}
                        className="text-[#1E3A8A] hover:bg-[#F0F0F0] p-1.5 inline-flex items-center gap-1 text-xs font-medium mr-1"
                        title="Edit contract"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setLinkingFor(c)}
                        data-testid={`link-invoices-${c.id}`}
                        className="text-[#1E3A8A] hover:bg-[#F0F0F0] p-1.5 inline-flex items-center gap-1 text-xs font-medium"
                        title="Link invoices"
                      >
                        <Link2 className="w-3.5 h-3.5" /> Link
                      </button>
                      <button
                        onClick={() => handleDelete(c.id)}
                        data-testid={`delete-contract-${c.id}`}
                        className="text-[#666] hover:text-[#DC2626] p-1.5 inline-flex"
                        title="Delete contract"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                  {expanded[c.id] && (
                    <tr className="bg-[#FAFAFA]" data-testid={`linked-invoices-${c.id}`}>
                      <td></td>
                      <td colSpan={9} className="py-3 px-4">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-2">
                          Linked Invoices ({c.invoice_count})
                        </div>
                        {c.linked_invoices.length === 0 ? (
                          <div className="text-xs text-[#666] py-2">No invoices linked. Click &ldquo;Link&rdquo; to add invoices from Zoho.</div>
                        ) : (
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-[#E5E5E5]">
                                <th className="text-left text-[10px] font-semibold uppercase tracking-wider text-[#666] py-2">Invoice #</th>
                                <th className="text-left text-[10px] font-semibold uppercase tracking-wider text-[#666] py-2">Date</th>
                                <th className="text-left text-[10px] font-semibold uppercase tracking-wider text-[#666] py-2">Status</th>
                                <th className="text-right text-[10px] font-semibold uppercase tracking-wider text-[#666] py-2">Total</th>
                                <th className="text-right text-[10px] font-semibold uppercase tracking-wider text-[#666] py-2">Paid</th>
                                <th className="text-right text-[10px] font-semibold uppercase tracking-wider text-[#666] py-2">Balance</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {c.linked_invoices.map((li) => li.missing ? (
                                <tr key={li.invoice_id}>
                                  <td colSpan={7} className="text-xs text-[#999] py-2 italic">Invoice {li.invoice_id} not found in current Zoho cache</td>
                                </tr>
                              ) : (
                                <tr key={li.invoice_id} className="border-b border-[#F0F0F0]">
                                  <td className="py-2 text-xs font-mono">{li.invoice_number}</td>
                                  <td className="py-2 text-xs font-mono text-[#666]">{li.date}</td>
                                  <td className="py-2"><StatusPill value={li.status} /></td>
                                  <td className="py-2 text-xs font-mono text-right">{fmtMoney(li.total, li.currency, 2)}</td>
                                  <td className="py-2 text-xs font-mono text-right text-[#047857]">{fmtMoney(li.paid, li.currency, 2)}</td>
                                  <td className="py-2 text-xs font-mono text-right text-[#E53E3E]">{li.balance > 0 ? fmtMoney(li.balance, li.currency, 2) : "—"}</td>
                                  <td className="py-2 text-right">
                                    <button
                                      onClick={() => handleUnlinkInvoice(c.id, li.invoice_id)}
                                      data-testid={`unlink-${c.id}-${li.invoice_id}`}
                                      className="text-[10px] text-[#666] hover:text-[#E53E3E] font-medium"
                                    >
                                      Unlink
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <ContractForm
          isEdit={!!editing}
          customers={customers}
          form={form}
          setForm={setForm}
          onCustomerSelect={onCustomerSelect}
          onSubmit={handleCreate}
          onClose={() => { setShowForm(false); setEditing(null); }}
        />
      )}

      {linkingFor && (
        <LinkInvoicesModal
          contract={linkingFor}
          onClose={() => setLinkingFor(null)}
          onLinked={async () => {
            const cid = linkingFor.id;
            setLinkingFor(null);
            await loadContracts();
            setExpanded((ex) => ({ ...ex, [cid]: true }));
          }}
        />
      )}
    </div>
  );
};

const ContractForm = ({ isEdit, customers, form, setForm, onCustomerSelect, onSubmit, onClose }) => (
  <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white border border-[#1E3A8A] w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="contract-form-modal">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-2xl font-bold tracking-tight">{isEdit ? "Edit" : "Add"} Contract / PO</h2>
        <button onClick={onClose} data-testid="close-contract-form"><X className="w-5 h-5" /></button>
      </div>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-[#666] mb-1.5 block">Customer</label>
          <select
            required
            value={form.customer_id}
            onChange={(e) => onCustomerSelect(e.target.value)}
            data-testid="contract-customer-select"
            className="w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm focus:border-[#0A0A0A] outline-none"
          >
            <option value="">-- Select customer --</option>
            {customers.map((c) => (
              <option key={c.customer_id} value={c.customer_id}>{c.customer_name} ({c.currency})</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-[#666] mb-1.5 block">PO / Contract Number</label>
          <input
            required
            value={form.po_number}
            onChange={(e) => setForm({ ...form, po_number: e.target.value })}
            data-testid="contract-po-number"
            placeholder="PO-2025-001"
            className="w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm font-mono focus:border-[#0A0A0A] outline-none"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#666] mb-1.5 block">Contract Value</label>
            <input
              required type="number" step="0.01"
              value={form.contract_value}
              onChange={(e) => setForm({ ...form, contract_value: e.target.value })}
              data-testid="contract-value"
              className="w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm font-mono focus:border-[#0A0A0A] outline-none"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#666] mb-1.5 block">Currency</label>
            <select
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
              data-testid="contract-currency"
              className="w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm focus:border-[#0A0A0A] outline-none"
            >
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#666] mb-1.5 block">Start Date</label>
            <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="contract-start-date"
              className="w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm font-mono focus:border-[#0A0A0A] outline-none" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#666] mb-1.5 block">End Date (optional)</label>
            <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="contract-end-date"
              className="w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm font-mono focus:border-[#0A0A0A] outline-none" />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-[#666] mb-1.5 block">Notes (optional)</label>
          <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="contract-notes"
            className="w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm focus:border-[#0A0A0A] outline-none" />
        </div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="flex-1 border border-[#0A0A0A] px-4 py-2 text-sm font-medium hover:bg-[#F5F5F5]">Cancel</button>
          <button type="submit" data-testid="contract-submit" className="flex-1 bg-[#1E3A8A] text-white px-4 py-2 text-sm font-medium hover:bg-[#172a63]">{isEdit ? "Save Changes" : "Save Contract"}</button>
        </div>
      </form>
    </div>
  </div>
);

const LinkInvoicesModal = ({ contract, onClose, onLinked }) => {
  const [available, setAvailable] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [scope, setScope] = useState("customer"); // customer | all
  const [loading, setLoading] = useState(true);

  const load = async (s) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (s === "customer" && contract.customer_id) params.set("customer_id", contract.customer_id);
      params.set("exclude_linked", "true");
      const r = await api.get(`/contracts/zoho-invoices?${params}`);
      setAvailable(r.data.invoices || []);
    } catch {
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(scope); }, [scope]);

  const toggle = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const handleLink = async () => {
    if (selected.size === 0) { toast.error("Select at least one invoice"); return; }
    try {
      await api.post(`/contracts/${contract.id}/invoices`, { invoice_ids: Array.from(selected) });
      toast.success(`${selected.size} invoice(s) linked`);
      onLinked();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to link");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-[#0A0A0A] w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="link-invoices-modal">
        <div className="p-6 border-b border-[#E5E5E5] flex items-center justify-between">
          <div>
            <h2 className="font-heading text-2xl font-bold tracking-tight">Link Invoices</h2>
            <div className="text-sm text-[#666] mt-1">to <span className="font-mono">{contract.po_number}</span> · {contract.customer_name}</div>
          </div>
          <button onClick={onClose} data-testid="close-link-modal"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
          <div className="text-xs font-mono text-[#666]">{available.length} unlinked invoices · {selected.size} selected</div>
          <div className="flex gap-1 text-xs">
            <button onClick={() => setScope("customer")} data-testid="scope-customer" className={`px-3 py-1 ${scope === "customer" ? "bg-[#0A0A0A] text-white" : "border border-[#E5E5E5]"}`}>
              This Customer
            </button>
            <button onClick={() => setScope("all")} data-testid="scope-all" className={`px-3 py-1 ${scope === "all" ? "bg-[#0A0A0A] text-white" : "border border-[#E5E5E5]"}`}>
              All
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-center text-sm text-[#666] py-12">Loading...</div>
          ) : available.length === 0 ? (
            <div className="text-center text-sm text-[#666] py-12">
              No unlinked invoices {scope === "customer" ? "for this customer" : ""}.
              {scope === "customer" && <div className="mt-2"><button onClick={() => setScope("all")} className="underline">Try All →</button></div>}
            </div>
          ) : (
            <table className="w-full">
              <thead className="sticky top-0 bg-[#F5F5F5] border-b border-[#0A0A0A]">
                <tr>
                  <th className="w-10 py-2"></th>
                  <th className="text-left text-[10px] font-semibold uppercase tracking-wider text-[#666] py-2 px-3">Invoice #</th>
                  <th className="text-left text-[10px] font-semibold uppercase tracking-wider text-[#666] py-2 px-3">Customer</th>
                  <th className="text-left text-[10px] font-semibold uppercase tracking-wider text-[#666] py-2 px-3">Date</th>
                  <th className="text-left text-[10px] font-semibold uppercase tracking-wider text-[#666] py-2 px-3">Status</th>
                  <th className="text-right text-[10px] font-semibold uppercase tracking-wider text-[#666] py-2 px-3">Total</th>
                  <th className="text-right text-[10px] font-semibold uppercase tracking-wider text-[#666] py-2 px-3">USD</th>
                </tr>
              </thead>
              <tbody>
                {available.map((inv) => (
                  <tr
                    key={inv.invoice_id}
                    onClick={() => toggle(inv.invoice_id)}
                    data-testid={`invoice-option-${inv.invoice_id}`}
                    className={`border-b border-[#E5E5E5] cursor-pointer ${selected.has(inv.invoice_id) ? "bg-[#0A0A0A]/5" : "hover:bg-[#F9F9F9]"}`}
                  >
                    <td className="py-2 px-3">
                      <input type="checkbox" checked={selected.has(inv.invoice_id)} readOnly className="accent-black" />
                    </td>
                    <td className="py-2 px-3 text-xs font-mono">{inv.invoice_number}</td>
                    <td className="py-2 px-3 text-xs">{inv.customer_name}</td>
                    <td className="py-2 px-3 text-xs font-mono text-[#666]">{inv.date}</td>
                    <td className="py-2 px-3"><StatusPill value={inv.status} /></td>
                    <td className="py-2 px-3 text-xs font-mono text-right">{fmtMoney(inv.total, inv.currency, 2)}</td>
                    <td className="py-2 px-3 text-xs font-mono text-right">{fmtMoney(inv.total_usd, "USD")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="p-4 border-t border-[#E5E5E5] flex gap-3 justify-end">
          <button onClick={onClose} className="border border-[#0A0A0A] px-4 py-2 text-sm font-medium hover:bg-[#F5F5F5]">Cancel</button>
          <button
            onClick={handleLink}
            disabled={selected.size === 0}
            data-testid="link-confirm-button"
            className="bg-[#0A0A0A] text-white px-4 py-2 text-sm font-medium hover:bg-[#333] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Link {selected.size} invoice{selected.size === 1 ? "" : "s"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Contracts;
